import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCart } from "../../Redux/CartSlice";

import {
  getCartByUserId,
  updateCartOnServer,
} from "../../services/Cart/CartPage";

import PaymentMethod from "./PaymentMethod";
import Slots from "./Slots";

import { useLocationContext } from "../../context/LocationContext";
import { UserContext } from "../../context/UserContext";

import LoginPopup from "../../component/common/LoginPopup";
import { X, Minus, Plus, Edit2, Ticket, Check } from "lucide-react";

import fallbackImg from "../assets/logo/appstore.png";
import Address from "../../component/Profile/Address/Index";

/* ================= REUSABLE COUPON SUB-COMPONENT ================= */
const CouponSection = ({ userId, totalAmount, onApplyCoupon, appliedCoupon }) => {
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const fetchAvailableCoupons = async () => {
      setCouponLoading(true);
      try {
        const response = await fetch(`https://admintest.medrphalabs.com/api/Coupan/GetAllCoupon?userId=${userId}&labId=1`);
        if (response.ok) {
          const data = await response.json();
          console.log("🎟️ Coupons API Response in Cart:", data);
          setCoupons(data.MainCoupons || data.UserCoupons || []);
        }
      } catch (err) {
        console.error("❌ FETCH COUPONS ERROR:", err);
      } finally {
        setCouponLoading(false);
      }
    };

    fetchAvailableCoupons();
  }, [userId]);

  if (coupons.length === 0 && !couponLoading) return null;

  return (
    <div className="mb-5 bg-white border rounded-xl p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
        <Ticket className="text-blue-500" size={16} />
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
          Available Coupons
        </h4>
      </div>

      {couponLoading ? (
        <p className="text-xs text-gray-400 text-center py-2 animate-pulse">Loading amazing offers...</p>
      ) : (
        <div className="flex flex-col gap-2 max-h-[160px] overflow-y-auto pr-1">
          {coupons.map((coupon, idx) => {
            const isApplied = appliedCoupon?.Code === coupon.Code;
            return (
              <div 
                key={coupon.Id || idx}
                className={`flex items-center justify-between p-2.5 border rounded-lg transition-all 
                  ${isApplied 
                    ? 'border-green-500 bg-green-50/50' 
                    : 'border-dashed border-gray-300 bg-gray-50/50 hover:bg-gray-50'
                  }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs font-bold bg-white px-2 py-0.5 border rounded text-gray-800 shadow-sm">
                      {coupon.Code}
                    </span>
                    <span className="text-[11px] font-bold text-green-600">
                      {coupon.DiscountValue}% OFF
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 truncate">
                    {coupon.Description || "Save money on this booking"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onApplyCoupon(coupon)}
                  className={`text-[11px] font-bold px-3 py-1 rounded-md transition-colors ml-2
                    ${isApplied 
                      ? 'bg-green-600 text-white flex items-center gap-0.5' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  {isApplied ? (
                    <>
                      <Check size={10} /> Applied
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ================= MAIN CART DRAWER COMPONENT ================= */
export default function CartDrawer({ onClose }) {
  const dispatch = useDispatch();

  const cartItems = useSelector(
    (state) => state.cart.items || []
  );

  const [cartId, setCartId] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showAddressList, setShowAddressList] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  /* --- COUPON SELECTION STATES FOR CALCULATIONS --- */
  const [appliedCoupon, setAppliedCoupon] = useState(null); 
  const [discountAmount, setDiscountAmount] = useState(0);

  const { user } = useContext(UserContext);
  const userId = user?.userId;

  const {
    address,
    loading: locationLoading,
    setAddress,
  } = useLocationContext();

  const requiresSlot = cartItems.some(
    (item) =>
      Number(item.categoryId) === 2 ||
      Number(item.categoryId) === 3
  );

  /* ================= ADDRESS ================= */
  const handleSelectAddress = (selectedAddress) => {
    setAddress(selectedAddress);
    toast.success("Address selected successfully!");
    setShowAddressList(false);
  };

  /* ================= FETCH CART ================= */
  useEffect(() => {
    if (!userId) {
      setShowPopup(true);
      return;
    }

    setShowPopup(false);

    const fetchCart = async () => {
      try {
        const response = await getCartByUserId(userId);
        const { cartId, items } = response || {};
        setCartId(cartId || 0);
        dispatch(setCart(items || []));
      } catch (err) {
        console.error("❌ FETCH CART ERROR:", err);
      }
    };

    fetchCart();
  }, [dispatch, userId, user]);

  /* ================= TOTAL & DISCOUNT CALCULATION ================= */
  useEffect(() => {
    const total = cartItems.reduce(
      (sum, i) => sum + Number(i.TotalPrice || i.price * i.quantity || 0),
      0
    );
    setTotalAmount(total);

    if (appliedCoupon) {
      const discValue = Number(appliedCoupon.DiscountValue || 0);
      const calculatedDiscount = (total * discValue) / 100;
      setDiscountAmount(calculatedDiscount);
    } else {
      setDiscountAmount(0);
    }
  }, [cartItems, appliedCoupon]);

  /* ================= PARENT COUPON HANDLER ================= */
  const handleCouponToggle = (coupon) => {
    if (appliedCoupon?.Code === coupon.Code) {
      setAppliedCoupon(null);
      setDiscountAmount(0);
    } else {
      setAppliedCoupon(coupon);
      const discValue = Number(coupon.DiscountValue || 0);
      const calculatedDiscount = (totalAmount * discValue) / 100;
      setDiscountAmount(calculatedDiscount);
    }
  };

  const handleClose = () => {
    onClose && onClose();
  };

  /* ================= UPDATE QUANTITY FOR PRODUCTS ================= */
  const handleUpdateQuantity = async (item, change) => {
    const newQuantity = (item.quantity || 1) + change;
    
    if (newQuantity < 1) {
      await handleRemove(item);
      return;
    }

    setUpdatingItemId(item.productId);

    try {
      const payload = {
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: change,
        price: item.price,
        discount: item.discount || 0,
        coupon: appliedCoupon ? appliedCoupon.Code : "",
      };

      await updateCartOnServer(payload);
      const { items } = await getCartByUserId(userId);
      dispatch(setCart(items || []));
    } catch (err) {
      console.error("❌ UPDATE QUANTITY ERROR:", err);
    } finally {
      setUpdatingItemId(null);
    }
  };

  /* ================= REMOVE ================= */
  const handleRemove = async (item) => {
    try {
      const payload = {
        userId,
        productId: item.productId,
        categoryId: item.categoryId,
        totalQuantity: -1,
        price: item.price,
        discount: item.discount || 0,
        coupon: "",
      };

      await updateCartOnServer(payload);
      const { items } = await getCartByUserId(userId);
      dispatch(setCart(items || []));
    } catch (err) {
      console.error("❌ REMOVE ERROR:", err);
    }
  };

  /* ================= GROUP ITEMS ================= */
  const testServices = cartItems.filter((i) => Number(i.categoryId) === 2);
  const packages = cartItems.filter((i) => Number(i.categoryId) === 3);
  const products = cartItems.filter((i) => Number(i.categoryId) === 1 || (!i.categoryId || (i.categoryId !== 2 && i.categoryId !== 3)));

  /* ================= PRODUCT ITEM UI ================= */
  const renderProductItem = (item) => {
    const isUpdating = updatingItemId === item.productId;
    const quantity = item.quantity || 1;

    return (
      <div key={`${item.productId}-${item.categoryId}`} className="flex items-center gap-2 p-2 bg-white border rounded-lg shadow-sm mb-2">
        <img
          src={item.image ? item.image.startsWith("http") ? item.image : `https://admintest.medrphalabs.com/TestMasterImage/${item.image}` : fallbackImg}
          className="w-12 h-12 rounded-md object-cover border"
          alt="item"
          onError={(e) => { e.target.src = fallbackImg; }}
        />
        <div className="flex-1 min-w-0">
          <h6 className="text-xs font-semibold text-gray-800 truncate">{item.name || "Item"}</h6>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-gray-500">₹{item.price}</span>
            <span className="text-xs font-bold text-green-600">₹{item.TotalPrice || item.price * item.quantity}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => handleUpdateQuantity(item, -1)}
              disabled={isUpdating}
              className={`w-6 h-6 rounded-full flex items-center justify-center ${quantity <= 1 ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition-colors disabled:opacity-50`}
            >
              <Minus size={12} />
            </button>
            <span className="text-sm font-semibold min-w-[24px] text-center">{isUpdating ? "..." : quantity}</span>
            <button
              onClick={() => handleUpdateQuantity(item, 1)}
              disabled={isUpdating}
              className="w-6 h-6 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors flex items-center justify-center disabled:opacity-50"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ================= LAB TEST & PACKAGE ITEM UI ================= */
  const renderServiceItem = (item) => (
    <div key={`${item.productId}-${item.categoryId}`} className="flex items-center gap-2 p-2 bg-white border rounded-lg shadow-sm mb-2">
      <img
        src={item.image ? item.image.startsWith("http") ? item.image : `https://admintest.medrphalabs.com/TestMasterImage/${item.image}` : fallbackImg}
        className="w-12 h-12 rounded-md object-cover border"
        alt="item"
        onError={(e) => { e.target.src = fallbackImg; }}
      />
      <div className="flex-1 min-w-0">
        <h6 className="text-xs font-semibold text-gray-800 truncate">{item.name || "Item"}</h6>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] text-gray-500">₹{item.price}</span>
          <span className="text-xs font-bold text-green-600">₹{item.TotalPrice || item.price * item.quantity}</span>
        </div>
      </div>
      <button onClick={() => handleRemove(item)} className="text-[10px] px-2 py-1 bg-red-50 text-red-500 rounded-md">
        Remove
      </button>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="mb-5">
      <h4 className="text-sm font-bold text-gray-700 mb-2 border-b pb-1">{title}</h4>
      {children}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={handleClose} />

      <div onClick={(e) => e.stopPropagation()} className="fixed right-0 top-0 w-[420px] h-full bg-white z-50 flex flex-col">
        {/* HEADER */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h6 className="text-lg font-semibold">Cart</h6>
              <button
                onClick={() => setShowAddressList(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors border border-blue-200"
              >
                <Edit2 size={14} />
                Edit Address
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {locationLoading ? "Loading..." : address || "No address selected"}
            </p>
          </div>
          <button onClick={handleClose} className="ml-3"><X size={18} /></button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {requiresSlot && <Slots />}

          {cartItems.length === 0 && (
            <div className="text-center text-gray-400 mt-5">Your cart is empty</div>
          )}

          {products.length > 0 && <Section title="Products">{products.map(renderProductItem)}</Section>}
          {testServices.length > 0 && <Section title="Lab Tests">{testServices.map(renderServiceItem)}</Section>}
          {packages.length > 0 && <Section title="Packages">{packages.map(renderServiceItem)}</Section>}

          {/* ✅ CALLING THE EMBEDDED COUPON SECTION COMPONENT */}
          {cartItems.length > 0 && (
            <CouponSection 
              userId={userId} 
              totalAmount={totalAmount} 
              onApplyCoupon={handleCouponToggle} 
              appliedCoupon={appliedCoupon} 
            />
          )}

          {/* Applied Success Message */}
          {appliedCoupon && (
            <div className="mb-4 flex items-center justify-between bg-green-50 border border-green-200 text-green-800 text-xs px-2.5 py-1.5 rounded-lg mx-0.5">
              <span>Code <b>{appliedCoupon.Code}</b> applied! Saved <b>₹{discountAmount.toFixed(2)}</b></span>
              <button onClick={() => { setAppliedCoupon(null); setDiscountAmount(0); }} className="text-red-500 font-bold underline text-[10px]">Remove</button>
            </div>
          )}

          {cartItems.length > 0 && (
            <PaymentMethod
              totalAmount={totalAmount - discountAmount}
              cartItems={cartItems}
              userId={userId}
              appliedCouponCode={appliedCoupon?.Code || ""}
            />
          )}
        </div>
      </div>

      {showAddressList && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex justify-center items-center">
          <div className="bg-white w-[95%] md:w-[700px] rounded-xl p-4 max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setShowAddressList(false)} className="absolute top-3 right-3"><X size={18} /></button>
            <Address onSelectAddress={handleSelectAddress} />
          </div>
        </div>
      )}

      <LoginPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </>
  );
}